import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { StudioSwatchChips } from './StudioSwatchChips';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { OperationService } from '../../../services';
import type { OperationResult } from '../../../domain';
import {
  DEFAULT_GENERATE_STATE,
  buildGenerateRequest,
  type GenerateFormState,
} from './buildRequests';
import { useOperationMutation } from './useOperationMutation';
import { submitErrorMessage } from '../../../components/auth/signInRequiredError';
import { mergePromptWithSwatches, type StudioParams } from '../../shared/panels/swatchSelection';
import { useAgentResultStore } from '../../../store/agentResultStore';
import { ProviderModelPicker } from './ProviderModelPicker';

interface Props {
  ensureSession: () => Promise<string>;
  isCreatingSession?: boolean;
  /** Legacy F14b bracket-describe path; populated when Studio has palettes with
   *  unrecognized kinds (e.g. Fashion's `fabric`). Empty for Generic Studio. */
  swatchPrompt?: string;
  /** Structured Studio swatch projection (Bucket 2). Generic Studio populates
   *  this; the builder uses promptFragments + aspectRatio to reshape the
   *  request, and the chip row below the prompt shows what's active. */
  studioParams?: StudioParams;
  onResult?: (result: OperationResult) => void;
}

export const GeneratePanel: React.FC<Props> = ({
  ensureSession,
  isCreatingSession = false,
  swatchPrompt,
  studioParams,
  onResult,
}) => {
  const [state, setState] = useState<GenerateFormState>(DEFAULT_GENERATE_STATE);
  const [validationError, setValidationError] = useState<string | null>(null);

  const mutation = useOperationMutation(OperationService.generate);

  // Bucket 3c: when an agent result is pushed via "Use as prompt", seed it
  // into the prompt field. Single-slot store; consume clears the pending.
  const pendingAgentHandoff = useAgentResultStore(s => s.pending);
  useEffect(() => {
    if (!pendingAgentHandoff) return;
    const consumed = useAgentResultStore.getState().consumeHandoff();
    if (!consumed) return;
    setState(s => ({ ...s, prompt: consumed.promptText }));
  }, [pendingAgentHandoff]);

  const handleSubmit = async () => {
    try {
      const sid = await ensureSession();
      // The bracket-describe path still applies for unrecognized kinds the
      // structured projection didn't consume — keep it as a back-compat
      // suffix on the user's prompt before the structured builder runs.
      const promptWithBrackets = mergePromptWithSwatches(state.prompt, swatchPrompt ?? '');
      const request = buildGenerateRequest({ ...state, prompt: promptWithBrackets }, sid, studioParams);
      setValidationError(null);
      const result = await mutation.mutateAsync(request);
      onResult?.(result);
    } catch (err) {
      setValidationError(submitErrorMessage(err));
    }
  };

  // Aspect-ratio override is what determines the effective dims; show them in
  // the disabled-but-visible w/h fields so the user sees why the swatch wins.
  const effectiveWidth = studioParams?.aspectRatio?.width ?? state.width;
  const effectiveHeight = studioParams?.aspectRatio?.height ?? state.height;
  const ratioOverridden = !!studioParams?.aspectRatio;

  const promptHasFragments = (studioParams?.promptFragments?.length ?? 0) > 0;
  const submitDisabled =
    mutation.isPending ||
    isCreatingSession ||
    (state.prompt.trim().length === 0 && !promptHasFragments);

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <AutoAwesomeIcon fontSize="small" />
        <Typography variant="subtitle2">Generate</Typography>
      </Stack>

      <ProviderModelPicker
        model={state.model}
        onModelChange={m => setState(s => ({ ...s, model: m }))}
        disabled={mutation.isPending || isCreatingSession}
        label="Image model"
        opKind="TextToImage"
      />

      <TextField
        label="Prompt"
        value={state.prompt}
        onChange={e => setState(s => ({ ...s, prompt: e.target.value }))}
        multiline
        minRows={2}
        maxRows={5}
        size="small"
        fullWidth
        required={!promptHasFragments}
        helperText={swatchPrompt ? `Swatches applied: ${swatchPrompt}` : undefined}
      />

      <StudioSwatchChips studioParams={studioParams} />

      <TextField
        label="Negative prompt"
        value={state.negativePrompt}
        onChange={e => setState(s => ({ ...s, negativePrompt: e.target.value }))}
        size="small"
        fullWidth
      />

      {/* Responsive field grid — reflows 4→2→1 columns as the rail narrows
          instead of cramming four inputs onto one cramped row. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
          gap: 1,
          alignItems: 'start',
        }}
      >
        <TextField
          label="Width"
          type="number"
          value={effectiveWidth}
          onChange={e => setState(s => ({ ...s, width: Number(e.target.value) }))}
          size="small"
          fullWidth
          inputProps={{ min: 256, max: 4096, step: 64 }}
          disabled={ratioOverridden}
          helperText={ratioOverridden ? 'Set by Aspect Ratio swatch' : undefined}
        />
        <TextField
          label="Height"
          type="number"
          value={effectiveHeight}
          onChange={e => setState(s => ({ ...s, height: Number(e.target.value) }))}
          size="small"
          fullWidth
          inputProps={{ min: 256, max: 4096, step: 64 }}
          disabled={ratioOverridden}
        />
        <TextField
          label="Images"
          type="number"
          value={state.numImages}
          onChange={e => setState(s => ({ ...s, numImages: Number(e.target.value) }))}
          size="small"
          fullWidth
          inputProps={{ min: 1, max: 4, step: 1 }}
        />
        <TextField
          label="Seed"
          value={state.seed}
          onChange={e => setState(s => ({ ...s, seed: e.target.value }))}
          size="small"
          fullWidth
          placeholder="random"
        />
      </Box>

      {validationError && <Alert severity="warning">{validationError}</Alert>}
      {mutation.isError && (
        <Alert severity="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Generation failed.'}
        </Alert>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled}
          startIcon={
            mutation.isPending || isCreatingSession ? (
              <CircularProgress size={16} />
            ) : (
              <AutoAwesomeIcon />
            )
          }
        >
          {isCreatingSession ? 'Starting session…' : mutation.isPending ? 'Generating…' : 'Generate'}
        </Button>
      </Box>
    </Stack>
  );
};
